import type { Case, Enquiry } from '../types/admin'
import type { Quote } from '../types/quote'
import type { Invoice } from '../types/invoice'
import { dayDiff } from './adminDates'
import { effectiveStatus } from './quote'
import { isoDay } from './timer'

export interface AttentionItem {
  id: string
  text: string
  to: string
  tone: 'normal' | 'warn' | 'danger'
}

export const activeCaseStatuses = ['Planlagt', 'I gang'] as const

export function isActiveCase(item: Case): boolean {
  return (activeCaseStatuses as readonly string[]).includes(item.status)
}

/** Aktive sager med dato i dag først, derefter efter dato. */
export function sortActiveCases(cases: Case[]): Case[] {
  return [...cases].sort((a, b) => {
    const da = dayDiff(a.plannedDate) ?? 9999
    const db = dayDiff(b.plannedDate) ?? 9999
    const ra = da === 0 ? -1 : da
    const rb = db === 0 ? -1 : db
    return ra - rb
  })
}

/** Aktiv sag med dato i dag, uden timer registreret i dag og uden kørende timer. */
export function casesMissingHoursToday(cases: Case[], runningCaseId?: string): Case[] {
  const today = isoDay(new Date())
  return cases.filter(
    (item) =>
      isActiveCase(item) &&
      dayDiff(item.plannedDate) === 0 &&
      item.id !== runningCaseId &&
      !(item.entries ?? []).some((entry) => entry.type === 'Timer' && entry.date === today),
  )
}

export function attentionItems(input: {
  cases: Case[]
  enquiries: Enquiry[]
  quotes: Quote[]
  invoices: Invoice[]
  runningCaseId?: string
}): AttentionItem[] {
  const items: AttentionItem[] = []

  const newEnquiries = input.enquiries.filter((item) => item.status === 'Ny henvendelse').length
  if (newEnquiries > 0) {
    items.push({
      id: 'henvendelser',
      text: newEnquiries === 1 ? '1 ny henvendelse' : `${newEnquiries} nye henvendelser`,
      to: '/admin/henvendelser',
      tone: 'normal',
    })
  }

  casesMissingHoursToday(input.cases, input.runningCaseId).forEach((item) => {
    items.push({ id: `timer-${item.id}`, text: `Ingen timer i dag på "${item.title}"`, to: `/admin/sager/${item.id}`, tone: 'warn' })
  })

  input.quotes.forEach((quote) => {
    if (effectiveStatus(quote) !== 'Sendt') return
    const diff = dayDiff(quote.validUntil)
    if (diff === null || diff < 0 || diff > 3) return
    items.push({
      id: `tilbud-${quote.id}`,
      text: `Tilbud ${quote.quoteNumber} udløber ${diff === 0 ? 'i dag' : diff === 1 ? 'i morgen' : `om ${diff} dage`}`,
      to: `/admin/tilbud/${quote.id}`,
      tone: 'warn',
    })
  })

  input.invoices.forEach((invoice) => {
    if (invoice.status !== 'Sendt') return
    const diff = dayDiff(invoice.dueDate)
    if (diff === null || diff > 3) return
    items.push({
      id: `faktura-${invoice.id}`,
      text:
        diff < 0
          ? `Faktura ${invoice.invoiceNumber} er forfalden`
          : `Faktura ${invoice.invoiceNumber} forfalder ${diff === 0 ? 'i dag' : diff === 1 ? 'i morgen' : `om ${diff} dage`}`,
      to: `/admin/fakturaer/${invoice.id}`,
      tone: diff < 0 ? 'danger' : 'warn',
    })
  })

  return items
}
